import { useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { subscribeApiError } from "../utilities/apiErrorBus";
import { isSignOutInProgress, useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { LocationContext } from "../App";

// Mounted once inside <Router>. Listens to ApiError events emitted by the API
// layer and routes them to a single user-visible action:
//
//   401 → session expired → sign out, clear React Query cache, send to /login
//   403 → not authorized to see this resource → toast + safe redirect
//   404 → resource missing → redirect to /not-found
//
// A short debounce per status keeps a burst of failed queries from triggering
// multiple redirects/toasts.

const DEBOUNCE_MS = 1000;

const ApiErrorHandler = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const { signOut, user } = useAuth();
    const { translations } = useLanguage();
    const { previous } = useContext(LocationContext) || {};

    const lastHandled = useRef({});

    useEffect(() => {
        const shouldDebounce = (status) => {
            const now = Date.now();
            const previousAt = lastHandled.current[status] || 0;
            if (now - previousAt < DEBOUNCE_MS) return true;
            lastHandled.current[status] = now;
            return false;
        };

        const handle = (error) => {
            const status = error?.status;
            if (!status) return;
            if (shouldDebounce(status)) return;

            if (status === 401) {
                const signingOut = isSignOutInProgress();

                // Treat 401 as expired/invalid session. If there is no current user we
                // are already on a public page; just navigate to login without
                // redundant signOut work.
                if (user && !signingOut) {
                    signOut().catch(() => undefined);
                }
                queryClient.clear();
                history.replace(signingOut ? "/login" : "/login?msg=sessionExpired");
                return;
            }

            if (status === 403) {
                toast(
                    <p style={{ color: "#022548" }}>
                        {translations?.noAccess || "You don't have access to this content."}
                    </p>
                );
                const safePath = previous && previous !== history.location.pathname ? previous : "/events";
                history.replace(safePath);
                return;
            }

            if (status === 404) {
                history.replace("/not-found");
                return;
            }
        };

        return subscribeApiError(handle);
    }, [history, queryClient, signOut, translations, user, previous]);

    return null;
};

export default ApiErrorHandler;
